/**
 * CANAL-DE-VENTA — identidad del consumidor que muestra/vende apuestas.
 *
 * Un sorteo puede habilitar apuestas distintas según el canal por el que se
 * venden. Hoy hay dos canales canónicos en lotowheel (verificados en
 * `backend/src/games/lotowheel/lotowheel-apuestas.js:46-47` con los mismos
 * literales `'wallet'`/`'ticket'`):
 *
 *   - `wallet` — jugador identificado con saldo en plataforma (front-customer).
 *   - `ticket` — bearer al portador (POS Android, front-cashier, front-tv:
 *     la TV de un local físico se comporta como POS — muestra/anuncia las
 *     mismas apuestas que el cashier).
 *
 * Cada front declara su canal por construcción (constante hardcoded en el
 * bootstrap del cliente). El backend NO deduce el canal de la request; lo
 * recibe explícito.
 *
 * Mapeo cliente → canal:
 *
 *   POS Android     → CANAL_DE_VENTA_TICKET_AL_PORTADOR
 *   front-cashier   → CANAL_DE_VENTA_TICKET_AL_PORTADOR
 *   front-tv        → CANAL_DE_VENTA_TICKET_AL_PORTADOR
 *   front-customer  → CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO
 *
 * El helper `resolverApuestasHabilitadasParaCanalDeVenta(sorteoConfig, canal)`
 * busca la lista efectiva con fallback hacia atrás:
 *
 *   1. Si el sorteo declara `apuestasHabilitadasPorCanalDeVenta.<canal>`
 *      (forma nueva multi-canal), devuelve esa lista.
 *      hoy, la mayoría de sorteos la usan; misma lista para todos los canales).
 *   3. `null` cuando ningún campo está presente — significa "todas las
 *      apuestas que la receta ofrece están habilitadas" (sin restricción
 *      explícita del sorteo).
 *
 * Este shape multi-canal es ADITIVO: hasta que un sorteo declare
 * `apuestasHabilitadasPorCanalDeVenta`, el comportamiento es idéntico al de
 * hoy en todos los consumidores.
 *
 * `shared/` es ESM. El backend (CJS, Node 22) lo importa con `require`.
 */

// Literales canónicos del canal — coinciden 1:1 con los strings que ya emite el
// backend en `lotowheel-apuestas.js:46-47`. NO renombrar a "_DE_JUGADOR" o
// similar: el wire-format histórico es exactamente `'wallet'` y `'ticket'`.
export const CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO = 'wallet';
export const CANAL_DE_VENTA_TICKET_AL_PORTADOR = 'ticket';
// `vlt` es canal de PRODUCCIÓN, no una tercera procedencia del dinero. Una VLT
// puede apostar cash, pasarela, bonus o rejuego sin dejar de producir por este
// canal. No se añade a CANALES_DE_VENTA_VALIDOS hasta que el admin permita
// configurar un catálogo de apuestas distinto para VLT.
export const CANAL_DE_PRODUCCION_VLT = 'vlt';
// `b2b` es el canal de PRODUCCIÓN de nuestros juegos lanzados en la plataforma
// de un integrador (launch B2B): jugador con sesión y wallet EXTERNA. Igual que
// `vlt`, no es canal de venta — el catálogo de apuestas que ve el jugador B2B
// es el de `wallet`. El literal viaja hasta `lotowheel_pending_bets.production_channel`,
// donde discrimina la identidad durable de operación (CHECK + índice único).
export const CANAL_DE_PRODUCCION_B2B = 'b2b';

// Set de canales válidos para fail-fast del helper. `shared/` es frontera
// pública (CLAUDE.md §2A/§2C): los exports deben fallar duro ante inputs
// inválidos. Drift silencioso de canal → un consumidor podría ver más/menos
// apuestas que las debidas y nadie lo notaría hasta que un jugador reporte
// "no me deja apostar X" o "me ofrecen X que no debería estar".
const CANALES_DE_VENTA_VALIDOS = new Set([
    CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
    CANAL_DE_VENTA_TICKET_AL_PORTADOR,
]);

// Registro de canales válidos para COMISIÓN POR CANAL — SEPARADO del set de apuestas de arriba (ejes
// distintos: aquel decide qué apuestas se habilitan; éste, qué claves acepta comision_por_canal). Reusa
// los MISMOS literales canónicos ('wallet'/'ticket') — un solo nombre por canal (NO 'bearer'/'cuenta',
// que son nombres del eje serie-de-tickets para el mismo concepto). Crece INDEPENDIENTE del set de
// apuestas: agregar 'vlt'/'integrador' acá habilita su comisión sin tocar el flujo de apuestas. La clave
// 'default' del JSON comision_por_canal NO va acá — es el fallback agnóstico, se valida aparte.
// Money-path: validar las claves contra este set cierra el bug de typo (clave mal escrita → cobra el
// default silenciosamente; ver docs/MAPA_EJES_DE_CANAL.md §Riesgo).
export const CANALES_DE_PRODUCCION_VALIDOS = new Set([
    CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
    CANAL_DE_VENTA_TICKET_AL_PORTADOR,
    CANAL_DE_PRODUCCION_VLT,
    CANAL_DE_PRODUCCION_B2B,
]);

/**
 * EJE RTP — los canales que tienen un RTP objetivo propio, con el nombre de cada pieza que los cablea.
 *
 * Es un eje DISTINTO del de comisión, aunque comparta los identificadores: el RTP decide cuánto
 * devuelve el juego al jugador de ese canal; la comisión, cómo se reparte lo que queda. Un canal puede
 * estar en `CANALES_DE_PRODUCCION_VALIDOS` (cobra comisión) sin tener RTP propio — `b2b` es hoy ese
 * caso: el integrador opera su propia economía de premios y no configura un target acá.
 *
 * Una sola lista para todo el recorrido: declararla acá lleva el canal al fail-fast previo al giro, al
 * target ajustado por Crucero, a su histórico y al array que consume el resolver, sin enumerar canales
 * a mano en ninguno de esos sitios. `omegaKey` es el coeficiente de feedback del Crucero propio del
 * canal; `null` significa que ES el omega base (`crucero_omega`), y un canal sin el suyo lo hereda.
 */
export const CANALES_DEL_EJE_RTP = Object.freeze([
    Object.freeze({
        id: CANAL_DE_PRODUCCION_VLT,
        targetKey: 'rtpTargetVlt',
        apostadoKey: 'totalApostadoVlt',
        pagoSiGanaKey: 'pagoSiGanaVlt',
        targetAjustadoKey: 'rtpTargetAjustadoVlt',
        historicoKey: 'rtpHistoricoCanalVlt',
        omegaKey: 'crucero_omega_vlt',
        historicoGetter: 'getRtpHistoricoVentanaVlt',
        apostadoDelSorteoKey: 'totalApostadoVltSorteo',
        pagadoDelSorteoKey: 'totalPagadoVltSorteo',
    }),
    Object.freeze({
        id: CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
        targetKey: 'rtpTargetWallet',
        apostadoKey: 'totalApostadoWallet',
        pagoSiGanaKey: 'pagoSiGanaWallet',
        targetAjustadoKey: 'rtpTargetAjustadoWallet',
        historicoKey: 'rtpHistoricoCanalWallet',
        omegaKey: null,
        historicoGetter: 'getRtpHistoricoVentanaWallet',
        apostadoDelSorteoKey: 'totalApostadoWalletSorteo',
        pagadoDelSorteoKey: 'totalPagadoWalletSorteo',
    }),
    Object.freeze({
        id: CANAL_DE_VENTA_TICKET_AL_PORTADOR,
        targetKey: 'rtpTargetTicket',
        apostadoKey: 'totalApostadoTicket',
        pagoSiGanaKey: 'pagoSiGanaTicket',
        targetAjustadoKey: 'rtpTargetAjustadoTicket',
        historicoKey: 'rtpHistoricoCanalTicket',
        omegaKey: 'crucero_omega_ticket',
        historicoGetter: 'getRtpHistoricoVentanaTicket',
        apostadoDelSorteoKey: 'totalApostadoTicketSorteo',
        pagadoDelSorteoKey: 'totalPagadoTicketSorteo',
    }),
]);

/**
 * ALLOWLIST DEL CAJERO — qué canales puede ver una respuesta de caja. `b2b` NO está, y por contrato.
 *
 * Es obligatoria, no defensiva. `mapearComisionPorCanal` exporta TODOS los canales que recibe (su propio
 * comentario lo dice) y los mapas de producción del cajero se colapsan a escalares sumando todas sus
 * claves: en cuanto el mapa por canal se vuelva genérico, `b2b` llegaría al POS y además SUMARÍA en el
 * rédito estimado. Confiar en que un nodo B2B «normalmente» no tiene caja es una regla de datos, no una
 * garantía de contrato — y el aislamiento estructural B2B es justamente la otra mitad de esta garantía.
 *
 * Lista BLANCA, no negra: un canal nuevo no llega al cajero hasta que alguien lo agregue acá a propósito.
 */
export const CANALES_VISIBLES_PARA_EL_CAJERO = new Set([
    CANAL_DE_VENTA_TICKET_AL_PORTADOR,
    CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
    CANAL_DE_PRODUCCION_VLT,
]);

/**
 * Copia de un mapa indexado por canal quedándose SOLO con los canales que el cajero puede ver.
 *
 * No muta la entrada: el corte, el admin y la liquidación siguen viendo el mapa completo — este filtro es
 * de la frontera del cajero, no del motor.
 *
 * @param {Object<string, unknown>} mapaPorCanal - p.ej. el `porCanal` de la producción atribuida.
 * @returns {Object<string, unknown>} mapa nuevo, solo con las claves permitidas.
 */
export function soloCanalesVisiblesParaElCajero(mapaPorCanal) {
    // `shared/` es frontera pública: un mapa que no es objeto es un error del caller, no un caso a tolerar
    // en silencio — devolver `{}` escondería un canal perdido detrás de un total que da 0.
    if (mapaPorCanal === null || typeof mapaPorCanal !== 'object' || Array.isArray(mapaPorCanal)) {
        throw new TypeError('soloCanalesVisiblesParaElCajero: se esperaba un mapa indexado por canal');
    }
    const visible = {};
    for (const canal of Object.keys(mapaPorCanal)) {
        if (CANALES_VISIBLES_PARA_EL_CAJERO.has(canal)) visible[canal] = mapaPorCanal[canal];
    }
    return visible;
}

/**
 * Orden de las columnas de la matriz de producción: la matriz histórica Bilhetes|Saldo primero
 * (ticket, wallet) y después los canales de producción que se sumaron (vlt, b2b). Es orden de
 * PRESENTACIÓN; el set de validación de arriba no tiene orden que importe.
 */
export const ORDEN_DE_CANALES_EN_MATRIZ = Object.freeze([
    CANAL_DE_VENTA_TICKET_AL_PORTADOR,
    CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
    CANAL_DE_PRODUCCION_VLT,
    CANAL_DE_PRODUCCION_B2B,
]);

/**
 * COLUMNAS DE LA MATRIZ DE PRODUCCIÓN POR CANAL — qué canales pinta una matriz (POS, caja web, ficha,
 * cascada del admin) para un mapa de producción dado.
 *
 * Regla única para todos los fronts: ticket y wallet SIEMPRE (son la matriz histórica Bilhetes|Saldo,
 * la fila se oculta si ambos dan 0), y cualquier otro canal del SSOT SOLO si tuvo actividad en el
 * período — «ocultar ceros» es la regla de oro de esa matriz, y una columna VLT vacía en un PV sin
 * terminal sería ruido. El orden es el de `ORDEN_DE_CANALES_EN_MATRIZ`.
 *
 * @param {Object<string,{jugado?:number,pagado?:number}>|Array<{canal:string,jugado?:number,pagado?:number}>} produccionPorCanal
 *   mapa por canal (ficha, POS) o lista `[{canal, jugado, pagado}]` (relatorio/cascada). Un canal
 *   ausente vale 0.
 * @returns {string[]} ids de canal a pintar, en orden canónico.
 */
export function canalesDeMatrizParaMostrar(produccionPorCanal) {
    const mapa = _mapaPorCanalDesde(produccionPorCanal);
    const siempre = new Set([CANAL_DE_VENTA_TICKET_AL_PORTADOR, CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO]);
    const columnas = [];
    for (const canal of ORDEN_DE_CANALES_EN_MATRIZ) {
        const produccion = mapa[canal];
        const tuvoActividad = Boolean(produccion && ((produccion.jugado || 0) !== 0 || (produccion.pagado || 0) !== 0));
        if (siempre.has(canal) || tuvoActividad) columnas.push(canal);
    }
    return columnas;
}

/**
 * Normaliza las dos formas en que viaja la producción por canal a un mapa `{ [canal]: {jugado, pagado} }`.
 * `null`/`undefined` = sin producción (mapa vacío). Cualquier otra cosa es un error del caller.
 */
function _mapaPorCanalDesde(produccionPorCanal) {
    if (produccionPorCanal == null) return {};
    if (Array.isArray(produccionPorCanal)) {
        const mapa = {};
        for (const entrada of produccionPorCanal) {
            if (!entrada || typeof entrada.canal !== 'string') {
                throw new TypeError('canalesDeMatrizParaMostrar: cada entrada de la lista lleva su `canal`');
            }
            mapa[entrada.canal] = entrada;
        }
        return mapa;
    }
    if (typeof produccionPorCanal !== 'object') {
        throw new TypeError('canalesDeMatrizParaMostrar: se esperaba un mapa o una lista de producción por canal');
    }
    return produccionPorCanal;
}

/** Rango válido de un RTP objetivo, el mismo que declara el `configSchema` del resolver. */
export const RTP_TARGET_MINIMO = 0.5;
export const RTP_TARGET_MAXIMO = 0.98;

/** Un target de canal es utilizable si es un número finito dentro del rango declarado. */
export function esRtpTargetDeCanalValido(valor) {
    return (
        typeof valor === 'number' && Number.isFinite(valor) && valor >= RTP_TARGET_MINIMO && valor <= RTP_TARGET_MAXIMO
    );
}

/**
 * ¿Es `canal` un canal de PRODUCCIÓN válido para comisión? (no incluye 'default', que es fallback).
 * @param {string} canal
 * @returns {boolean}
 */
export function esCanalDeProduccionValido(canal) {
    return CANALES_DE_PRODUCCION_VALIDOS.has(canal);
}

/**
 * Resuelve el canal de PRODUCCIÓN de una operación (apuesta/premio) para contabilidad/comisión.
 * SSOT de la regla — hoy dispersa como `toEntityId(clienteId) === null ⇒ ticket` en lotowheel-apuestas.
 * El canal lo determina la NATURALEZA de la operación, no el dispositivo: una apuesta de jugador
 * IDENTIFICADO (con clienteId) es 'wallet'; una al PORTADOR (sin clienteId) es 'ticket'.
 *
 * Cascada extensible: hoy son dos reglas (identificado→wallet, portador→ticket). Un canal nuevo (vlt,
 * etc.) se agrega como una rama más acá, sin tocar los call-sites que ya invocan este resolver.
 *
 * @param {{ clienteId: number|string|null|undefined, canalProduccion?: string }} contextoOperacion - la apuesta/movimiento.
 *   `clienteId` que normaliza a un id válido (>0) ⇒ jugador identificado (wallet); si no ⇒ portador (ticket).
 * @returns {string} canal de producción canónico ('wallet' | 'ticket').
 */
export function resolverCanalDeProduccionDeOperacion(contextoOperacion) {
    const canalExplicito = contextoOperacion?.canalProduccion;
    if (canalExplicito != null) {
        if (!esCanalDeProduccionValido(canalExplicito)) {
            throw new TypeError(`Canal de producción desconocido: ${canalExplicito}`);
        }
        return canalExplicito;
    }
    const clienteId = contextoOperacion && contextoOperacion.clienteId;
    // Bit-exacto con el discriminador del juego (backend `toEntityId`, src/utils/safe-id.js): un id que
    // normaliza a entero > 0 es un jugador identificado (wallet); null/no-finito/≤0/string-no-numérico ⇒
    // portador (ticket). Se replica la regla acá —no se importa toEntityId— porque shared/ es ESM neutral
    // (lo consumen también los fronts) y no debe acoplar con módulos del backend.
    const idNormalizado = clienteId == null ? NaN : typeof clienteId === 'number' ? clienteId : parseInt(clienteId, 10);
    const tieneJugadorIdentificado = Number.isFinite(idNormalizado) && idNormalizado > 0;
    return tieneJugadorIdentificado ? CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO : CANAL_DE_VENTA_TICKET_AL_PORTADOR;
}

/**
 * Resuelve la lista efectiva de ids-de-apuesta habilitadas para un canal de
 * venta concreto, con fallback hacia atrás al shape plano legacy.
 *
 * Se llama desde cada front al armar wheel/mat/stats/prize-table/tiquete, y
 * desde el backend en `cashier-login-config` al armar el snapshot del cashier.
 *
 * Fail-fast con canal: pasar un canal desconocido (typo, undefined, string
 * que no es `'wallet'` ni `'ticket'`) lanza error. La función NO cae al
 * fallback silencioso lo enmascararía.
 *
 * @param {Object} sorteoConfig - snapshot del sorteo (puede traer cualquiera de
 *   las dos formas: `apuestasHabilitadas` o `apuestasHabilitadasPorCanalDeVenta`).
 * @param {string} canalDeVenta - una de las constantes `CANAL_DE_VENTA_*`.
 * @returns {string[]|null} ids-de-apuesta habilitados, o `null` si el sorteo no
 *   restringe nada (en cuyo caso aplican todas las apuestas de la receta).
 * @throws {Error} si `canalDeVenta` no es un canal canónico.
 */
export function resolverApuestasHabilitadasParaCanalDeVenta(sorteoConfig, canalDeVenta) {
    if (!CANALES_DE_VENTA_VALIDOS.has(canalDeVenta)) {
        throw new Error(
            `[canal-de-venta] resolverApuestasHabilitadasParaCanalDeVenta: canal de venta ` +
                `desconocido "${canalDeVenta}". Esperado uno de: ` +
                `'${CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO}' | ` +
                `'${CANAL_DE_VENTA_TICKET_AL_PORTADOR}'. ` +
                `Usar las constantes CANAL_DE_VENTA_* de este módulo, no literales.`
        );
    }
    // Si el sorteo declara la forma multi-canal, ES la fuente autoritativa
    // — incluso si la clave del canal pedido vale `null`. Un `null` ahí
    // significa "el operador eligió que ESTE canal no esté restringido",
    // intención explícita distinta del fallback al campo plano legacy.
    // Caer al fallback heredaría la lista plana de otra época y romperia
    // la intención del operador (§8.14, hallazgo #4 del code-review).
    const apuestasPorCanal = sorteoConfig && sorteoConfig.apuestasHabilitadasPorCanalDeVenta;
    if (apuestasPorCanal && canalDeVenta in apuestasPorCanal) {
        const valorDelCanal = apuestasPorCanal[canalDeVenta];
        return Array.isArray(valorDelCanal) ? valorDelCanal : null;
    }
    // Sin forma multi-canal — fallback al campo plano legacy.
    if (sorteoConfig && Array.isArray(sorteoConfig.apuestasHabilitadas)) {
        return sorteoConfig.apuestasHabilitadas;
    }
    return null;
}

/**
 * Decide cómo PERSISTIR las listas de apuestas habilitadas que el admin
 * declaró por canal. Espejo lógico del resolver: el resolver lee, este
 * helper escribe.
 *
 * Política (§8.14):
 *
 *   1. Si AMBAS listas son `null` (todas habilitadas en ambos canales):
 *      `{ apuestasHabilitadas: null, apuestasHabilitadasPorCanalDeVenta: null }`.
 *      Sorteo sin restricción explícita.
 *
 *   2. Si las dos listas son SEMÁNTICAMENTE IGUALES (mismo set de ids,
 *      compatibilidad con consumidores que solo lean `apuestasHabilitadas`
 *      y evita escribir multi-canal cuando no aporta nada. Resultado:
 *      `{ apuestasHabilitadas: [ids ordenados], apuestasHabilitadasPorCanalDeVenta: null }`.
 *
 *   3. Si DIFIEREN (incluido el caso uno null + el otro lista): escribir
 *      forma MULTI-CANAL. El campo plano se borra para no dejar basura
 *      con dos verdades coexistiendo. Resultado:
 *      `{ apuestasHabilitadas: null, apuestasHabilitadasPorCanalDeVenta: { ticket: [...], wallet: [...] } }`.
 *      Cada canal con su lista canonicalizada (ordenada). `null` se preserva
 *      como `null` en su slot (significa "sin restricción para ese canal").
 *
 * Por qué normalizar a plana cuando son iguales: el contrato del sorteo
 * tiene DOS shapes válidos para el mismo estado lógico. Si admin escribe
 * multi-canal con listas idénticas, ningún consumidor cambia su comportamiento
 * `apuestasHabilitadasPorCanalDeVenta` nuevo). El siguiente edit puede leer
 * cualquiera y diferir — drift latente. Una sola forma canónica por estado.
 *
 * @param {string[]|null} apuestasHabilitadasParaCanalTicket
 * @param {string[]|null} apuestasHabilitadasParaCanalWallet
 * @returns {{ apuestasHabilitadas: string[]|null, apuestasHabilitadasPorCanalDeVenta: Object|null }}
 */
export function normalizarApuestasHabilitadasParaPersistencia(
    apuestasHabilitadasParaCanalTicket,
    apuestasHabilitadasParaCanalWallet
) {
    const ticket = _normalizarListaApuestasHabilitadasOPreservarNull(apuestasHabilitadasParaCanalTicket);
    const wallet = _normalizarListaApuestasHabilitadasOPreservarNull(apuestasHabilitadasParaCanalWallet);

    // Caso 1: ambos null → sorteo sin restricción.
    if (ticket === null && wallet === null) {
        return { apuestasHabilitadas: null, apuestasHabilitadasPorCanalDeVenta: null };
    }

    // Caso 2: ambos son listas con el mismo contenido → forma plana.
    if (ticket !== null && wallet !== null && _sonListasIgualesComoSets(ticket, wallet)) {
        return { apuestasHabilitadas: ticket, apuestasHabilitadasPorCanalDeVenta: null };
    }

    // Caso 3: difieren (incluido uno null + otro lista) → forma multi-canal.
    return {
        apuestasHabilitadas: null,
        apuestasHabilitadasPorCanalDeVenta: {
            [CANAL_DE_VENTA_TICKET_AL_PORTADOR]: ticket,
            [CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO]: wallet,
        },
    };
}

// Devuelve `null` si la entrada no es array o es array vacío "marcador de
// nada"; sino devuelve la lista ordenada (sort lexicográfico) y deduplicada.
// El orden canónico permite comparación bit-a-bit entre canales y evita
// que el snapshot cambie por reordenamiento espurio del DOM.
function _normalizarListaApuestasHabilitadasOPreservarNull(lista) {
    if (!Array.isArray(lista)) return null;
    if (lista.length === 0) return [];
    const set = new Set(lista.filter(id => typeof id === 'string'));
    return [...set].sort();
}

// Compara dos listas que YA VIENEN canonicalizadas (sort + dedup vía
// `_normalizarListaApuestasHabilitadasOPreservarNull`). En ese caso la
// igualdad como sets es igualdad elemento-a-elemento en el mismo orden.
// La función es privada del módulo — el único caller es el helper de
// abajo, así que la pre-canonicalización es invariante del módulo.
function _sonListasIgualesComoSets(listaA, listaB) {
    if (listaA.length !== listaB.length) return false;
    for (let i = 0; i < listaA.length; i++) {
        if (listaA[i] !== listaB[i]) return false;
    }
    return true;
}
